import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';

import MachinesWidget from '../../components/Dashboard/MachinesWidget';
import VendorBreakdownWidget from '../../components/Dashboard/VendorBreakdownWidget';
import MaintenanceAlertsWidget from '../../components/Dashboard/MaintenanceAlertsWidget';
import MaintenanceAlertsByMachineWidget from '../../components/Dashboard/MaintenanceAlertsByMachineWidget';
import MonitorMultiparametrico from '../../components/Dashboard/MonitorMultiparametrico';
import OperationalNotesWidget from '../../components/Dashboard/OperationalNotesWidget';

import type { DashboardAggregates } from '../../types/machinesDashboard';
import type { DashboardResponseDto } from '../../types/dashboard';
import { dashboardApi } from '../../api/dashboardApi';
import { alarmsApi } from '../../api/alarmsApi';
import { useAuth } from '../../auth/AuthContext';
import { useMachinesLive } from '../../hooks/useMachinesLive';
import '../../styles/dashboard.css';

type PresenceRow = {
  name: string;
  role: string;
  inTime: string;
  outTime?: string;
  status: 'PRESENTE' | 'ASSENTE' | 'IN_RITARDO' | 'USCITO';
};

// ====== NUOVI TYPES (Allarmi + Telemetria) ======
type AlarmRow = {
  id: string;
  machineId: string;
  machineName?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | string;
  title?: string;
  code?: string;
  ts: string; // ISO
  acknowledged?: boolean;
  resolvedAt?: string | null;
};

type TelemetryLatestRow = {
  machineId: string;
  machineName?: string;
  metric: string; // es TEMP_CABINET
  value: number;
  unit?: string;
  ts: string; // ISO
};

type TelemetryBucketRow = {
  bucket: string; // ISO
  value: number;
};

export default function DashboardPage() {
  // ====== AUTH TOKEN ======
  const { token } = useAuth() as any;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [data, setData] = useState<DashboardResponseDto | null>(null);

  // ====== LIVE: socket overlay per aggiornamenti real-time ======
  const { liveOverlay, connected } = useMachinesLive();

  // ====== NUOVO: allarmi + telemetria dal dashboard ======
  const alarmsActive: AlarmRow[] = (data as any)?.alarmsActive ?? [];
  const telemetryLatest = useMemo<TelemetryLatestRow[]>(
    () => (data as any)?.telemetryLatest ?? [],
    [data],
  );

  // Merge live overlay: usa valori socket se presenti
  const telemetryDisplay = useMemo(() => {
    return telemetryLatest.map((t) => {
      const live = liveOverlay[t.machineId];
      if (!live) return t;
      const liveTemp =
        typeof live.actualTemperatureChamber === 'number'
          ? live.actualTemperatureChamber
          : typeof live.actualInternalCoreTemperature === 'number'
            ? live.actualInternalCoreTemperature
            : null;
      return {
        ...t,
        value: liveTemp ?? t.value,
        ts: live.timestamp ?? t.ts,
        isLive: liveTemp != null || live.status != null,
        status: live.status,
      };
    });
  }, [telemetryLatest, liveOverlay]);

  // ====== NUOVO: stato grafico storico temperature ======
  const [selectedMachineId, setSelectedMachineId] = useState<string>('');
  const [chartRange, setChartRange] = useState<'6h' | '24h'>('6h');
  const [chartMetric, setChartMetric] = useState('TEMPERATURE_C');
  const [chartLoading, setChartLoading] = useState(false);
  const [chartData, setChartData] = useState<TelemetryBucketRow[]>([]);

  // Programma macchina (Rational)
  const [programDetail, setProgramDetail] = useState<{
    currentProgram?: string;
    programPhase?: string;
    remainingTime?: string;
  } | null>(null);

  // Gestione ACK/RESET da card "Allarmi attivi"
  const [hiddenAlarmIds, setHiddenAlarmIds] = useState<Set<string>>(new Set());
  const [processingAlarmIds, setProcessingAlarmIds] = useState<Set<string>>(new Set());

  const visibleAlarms = alarmsActive.filter((a) => !hiddenAlarmIds.has(a.id));

  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
  };

  const minsAgo = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (Number.isNaN(diff)) return '—';
    return Math.max(0, Math.round(diff / 60000));
  };

  const handleAckResetAlarm = async (alarmId: string) => {
    if (!token) {
      alert('Sessione scaduta: rifai login per gestire gli allarmi.');
      return;
    }

    setHiddenAlarmIds((prev) => {
      const next = new Set(prev);
      next.add(alarmId);
      return next;
    });

    setProcessingAlarmIds((prev) => {
      const next = new Set(prev);
      next.add(alarmId);
      return next;
    });

    try {
      await alarmsApi.ackReset(token, alarmId, {
        note: 'ACK/RESET da dashboard',
        force: false,
      });
    } catch {
      alert('Errore ACK/RESET allarme.');

      // Se fallisce, rimostro l'allarme
      setHiddenAlarmIds((prev) => {
        const next = new Set(prev);
        next.delete(alarmId);
        return next;
      });
    } finally {
      setProcessingAlarmIds((prev) => {
        const next = new Set(prev);
        next.delete(alarmId);
        return next;
      });
    }
  };

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await dashboardApi.getDashboard(token);
      setData(res);
    } catch (e: any) {
      setError(e?.message ?? 'Errore caricamento dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== refresh automatico (opzionale ma utile per “live”) ======
  useEffect(() => {
    const id = setInterval(() => {
      void load();
    }, 60 * 60 * 1000); // 1 ora
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== Seleziona macchina per grafico (prima disponibile) ======
  useEffect(() => {
    if (!selectedMachineId && telemetryLatest.length > 0) {
      const first = telemetryLatest[0];
      if (first) setSelectedMachineId(first.machineId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetryLatest.length]);

  // ====== Carica storico telemetria (bucket) ======
  async function loadTelemetryChart(machineId: string, metric: string, range: '6h' | '24h') {
    if (!machineId) return;
    if (!token) return;

    setChartLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - (range === '24h' ? 24 : 6) * 60 * 60 * 1000);

      const bucket = range === '24h' ? '1h' : '5m';
      const items = await dashboardApi.getMachineTelemetry(token, machineId, {
        metric,
        from: from.toISOString(),
        to: to.toISOString(),
        bucket,
      });

      const arr = Array.isArray(items) ? items : items?.items ?? [];
      setChartData(arr);
    } catch {
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  }

  useEffect(() => {
    void loadTelemetryChart(selectedMachineId, chartMetric, chartRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachineId, chartMetric, chartRange]);

  // Carica programma Rational quando selezionata macchina RATIONAL
  useEffect(() => {
    if (!selectedMachineId || !token) {
      setProgramDetail(null);
      return;
    }
    const items = (data as any)?.machinesDashboard?.items ?? [];
    const item = items.find((m: any) => m.id === selectedMachineId);
    const provider = String(item?.provider ?? '').toUpperCase();
    if (provider !== 'RATIONAL') {
      setProgramDetail(null);
      return;
    }
    let cancelled = false;
    dashboardApi.getRationalMachineDetail(token, selectedMachineId).then((res) => {
      if (cancelled) return;
      const p = (res as any)?.program ?? {};
      setProgramDetail({
        currentProgram: p.currentProgram ?? p.name ?? p.program ?? '-',
        programPhase: p.programPhase ?? p.phase ?? '-',
        remainingTime: p.remainingTime ?? p.remaining ?? '-',
      });
    }).catch(() => {
      if (!cancelled) setProgramDetail(null);
    });
    return () => { cancelled = true; };
  }, [selectedMachineId, token, data]);

  // ====== Serie grafici CASSA (restano mock finché non colleghi POS) ======
  const salesSeries = useMemo(
    () => [
      { day: 'Lun', total: 420 },
      { day: 'Mar', total: 610 },
      { day: 'Mer', total: 530 },
      { day: 'Gio', total: 720 },
      { day: 'Ven', total: 900 },
      { day: 'Sab', total: 1065 },
      { day: 'Dom', total: 780 },
    ],
    [],
  );

  const paymentsSeries = useMemo(
    () => [
      { name: 'Contanti', value: 520 },
      { name: 'Bancomat', value: 545 },
    ],
    [],
  );

  // ====== Presenze (mock per ora) ======
  const presences: PresenceRow[] = [
    { name: 'Mario Rossi', role: 'Operatore', inTime: '08:02', status: 'PRESENTE' },
    { name: 'Giulia Bianchi', role: 'Operatore', inTime: '08:18', status: 'IN_RITARDO' },
    { name: 'Luca Verdi', role: 'Magazzino', inTime: '07:55', outTime: '14:10', status: 'USCITO' },
    { name: 'Sara Neri', role: 'Amministrazione', inTime: '-', status: 'ASSENTE' },
  ];

  // ====== Dati MACCHINARI reali (aggregati dashboard) ======
  const machinesData = ((data as any)?.machinesDashboard ?? null) as DashboardAggregates | null;

  // KPI “macchine” reali (se data c’è)
  const machinesOnline = machinesData?.totals.online ?? 0;
  const alarmsOpen = machinesData?.totals.alarmsOpen ?? 0;
  const offline = machinesData?.totals.offline ?? 0;
  const criticalUnacked = machinesData?.totals.criticalUnacked ?? 0;

  // KPI cassa (mock per ora)
  const eur = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const salesToday = 1065;
  const salesMonth = 18940;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="h4 mb-0">Dashboard</h1>
        <div className="d-flex gap-2 align-items-center">
          <span className="small text-muted">Dati aggiornati ogni ora</span>
          <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Aggiorno…' : 'Aggiorna'}
          </button>
          <Link to="/cash" className="btn btn-primary btn-sm">
            Apri Cassa
          </Link>
          <Link to="/personnel" className="btn btn-outline-secondary btn-sm">
            Apri Personale
          </Link>
        </div>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="alert alert-secondary" role="alert">
          Caricamento…
        </div>
      ) : null}

      {/* Notifiche manutenzione (lampeggianti) */}
      <MaintenanceAlertsWidget />

      {/* KPI */}
      <div className="row g-3">
        <KpiCard title="Incasso oggi" value={eur(salesToday)} icon="sales" variant="primary" />
        <KpiCard title="Incasso mese" value={eur(salesMonth)} icon="sales" variant="success" />
        <KpiCard title="Macchine online" value={`${machinesOnline}`} icon="machines" variant="success" />
        <KpiCard title="Allarmi aperti" value={`${alarmsOpen}`} icon="alarms" variant={alarmsOpen > 0 ? 'danger' : 'success'} />
      </div>

      <div className="row g-3 mt-0">
        {/* Grafici CASSA */}
        <div className="col-12 col-xl-8">
          <div className="card h-100">
            <div className="card-header fw-semibold">Trend incassi (ultima settimana)</div>
            <div className="card-body" style={{ height: 280, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height={280} minHeight={200}>
                <AreaChart data={salesSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="total" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card h-100">
            <div className="card-header fw-semibold">Pagamenti (oggi)</div>
            <div className="card-body" style={{ height: 280, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height={280} minHeight={200}>
                <BarChart data={paymentsSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* MACCHINARI */}
        <div className="col-12 dashboard-fade-in">
          <div className="dashboard-card-modern">
            <div className="dashboard-card-header-modern d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-cpu fs-5 dashboard-gradient-text" />
                <span className="fw-semibold">Macchinari</span>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <span className="dashboard-badge-modern text-bg-success">Online: {machinesOnline}</span>
                <span className="dashboard-badge-modern text-bg-warning">Offline: {offline}</span>
                <span className="dashboard-badge-modern text-bg-danger">Allarmi: {alarmsOpen}</span>
                {criticalUnacked > 0 && (
                  <span className="dashboard-badge-modern text-bg-danger dashboard-pulse">Critici: {criticalUnacked}</span>
                )}
              </div>
            </div>

            <div className="card-body">
              {!machinesData ? (
                <div className="text-muted">Nessun dato macchine disponibile.</div>
              ) : (
                <>
                  <MachinesWidget data={machinesData} />

                  <div className="row g-3 mt-0">
                    <div className="col-12 col-xl-4">
                      <VendorBreakdownWidget data={machinesData} />
                    </div>
                    <div className="col-12 col-xl-4">
                      <MaintenanceAlertsByMachineWidget />
                    </div>
                    <div className="col-12 col-xl-4">
                      <OperationalNotesWidget />
                    </div>
                  </div>
                </>
              )}

              <div className="mt-3 d-flex gap-2">
                <button className="btn btn-outline-primary btn-sm" disabled>
                  Vai a Macchine (in arrivo)
                </button>
                <button className="btn btn-outline-danger btn-sm" disabled>
                  Vai ad Allarmi (in arrivo)
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ====== Monitor Multiparametrico: qualità linea ====== */}
        <div className="col-12">
          <MonitorMultiparametrico />
        </div>

        {/* ====== ALLARMI + TEMPERATURE + GRAFICO ====== */}
        <div className="col-12">
          <div className="row g-3">
            {/* Allarmi attivi */}
            <div className="col-12 col-xl-6 dashboard-fade-in">
              <div className="dashboard-card-modern h-100">
                <div className="dashboard-card-header-modern d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle-fill text-danger" />
                    <span className="fw-semibold">Allarmi attivi</span>
                  </div>
                  <span className="dashboard-badge-modern text-bg-danger">{visibleAlarms.length}</span>
                </div>

                <div className="card-body">
                  {visibleAlarms.length === 0 ? (
                    <div className="text-muted">Nessun allarme attivo 🎉</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0 dashboard-table-modern">
                        <thead>
                          <tr>
                            <th>Sev</th>
                            <th>Macchina</th>
                            <th>Evento</th>
                            <th>Ora</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleAlarms.slice(0, 8).map((a) => {
                            const busy = processingAlarmIds.has(a.id);
                            return (
                              <tr key={a.id}>
                                <td>
                                  <AlarmSeverityBadge severity={a.severity} />
                                </td>
                                <td className="fw-semibold">{a.machineName ?? a.machineId}</td>
                                <td className="text-muted">{a.title ?? a.code ?? 'Allarme'}</td>
                                <td>{fmtDate(a.ts)}</td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={busy}
                                    onClick={() => handleAckResetAlarm(a.id)}
                                  >
                                    {busy ? 'Spengo…' : 'Spegni'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mt-3 d-flex gap-2">
                    <button className="btn btn-outline-danger btn-sm" disabled>
                      Vai ad Allarmi (in arrivo)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Temperature live + Programma */}
            <div className="col-12 col-xl-6 dashboard-fade-in">
              <div className="dashboard-temperature-card h-100">
                <div className="dashboard-card-header-gradient d-flex align-items-center justify-content-between py-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-thermometer-half fs-5" />
                    <span className="fw-semibold">Temperature live</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark">{telemetryLatest.length} macchine</span>
                    {connected ? (
                      <span className="badge bg-success">
                        <i className="bi bi-broadcast me-1" />
                        WebSocket LIVE
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        <i className="bi bi-wifi-off me-1" />
                        Disconnesso
                      </span>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  {telemetryDisplay.length === 0 ? (
                    <div className="text-muted text-center py-4">Nessun dato telemetria disponibile.</div>
                  ) : (
                    <div className="vstack gap-2">
                      {telemetryDisplay.slice(0, 8).map((t, idx) => (
                        <div
                          key={`${t.machineId}-${t.metric}-${idx}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedMachineId(t.machineId)}
                          onKeyDown={(e) => e.key === 'Enter' && setSelectedMachineId(t.machineId)}
                          className={`dashboard-temperature-item ${selectedMachineId === t.machineId ? 'active' : ''}`}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-semibold">{t.machineName ?? t.machineId}</span>
                                {(t as any).isLive && (
                                  <span className="badge bg-success bg-opacity-25 text-success small">LIVE</span>
                                )}
                                {(t as any).status && (
                                  <span className={`badge small ${
                                    (t as any).status === 'ONLINE' ? 'bg-success' :
                                    (t as any).status === 'OFFLINE' ? 'bg-secondary' : 'bg-warning'
                                  }`}>
                                    {(t as any).status}
                                  </span>
                                )}
                              </div>
                              <div className="small text-muted">
                                {(t as any).isLive ? 'in tempo reale' : `agg. ${minsAgo(t.ts)} min fa`}
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="dashboard-temperature-value">
                                {t.value}
                                {t.unit ? ` ${t.unit}` : '°C'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Programma (Rational) */}
                  {programDetail && selectedMachineId && (
                    <div
                      className="mt-3 rounded-3 p-3"
                      style={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      <div className="small fw-semibold text-success mb-2">
                        <i className="bi bi-play-circle me-1" /> Programma attuale
                      </div>
                      <div className="row g-2 small">
                        <div className="col-4">
                          <span className="text-muted">Programma:</span>
                          <div className="fw-semibold">{String(programDetail.currentProgram ?? '-')}</div>
                        </div>
                        <div className="col-4">
                          <span className="text-muted">Fase:</span>
                          <div className="fw-semibold">{String(programDetail.programPhase ?? '-')}</div>
                        </div>
                        <div className="col-4">
                          <span className="text-muted">Rimanente:</span>
                          <div className="fw-semibold">{String(programDetail.remainingTime ?? '-')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Controlli grafico */}
                  <div className="mt-3 d-flex gap-2 flex-wrap align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 200 }}
                      value={selectedMachineId}
                      onChange={(e) => setSelectedMachineId(e.target.value)}
                      disabled={telemetryLatest.length === 0}
                    >
                      {Array.from(
                        new Map(
                          telemetryLatest.map((x) => [x.machineId, x.machineName ?? x.machineId] as const),
                        ).entries(),
                      ).map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 160 }}
                      value={chartMetric}
                      onChange={(e) => setChartMetric(e.target.value)}
                    >
                      <option value="TEMPERATURE_C">Temperatura (°C)</option>
                      <option value="TEMP_CABINET">Temp. camera</option>
                      <option value="HUMIDITY_PCT">Umidità (%)</option>
                      <option value="PRESSURE_BAR">Pressione (bar)</option>
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 140 }}
                      value={chartRange}
                      onChange={(e) => setChartRange(e.target.value as '6h' | '24h')}
                    >
                      <option value="6h">6 ore (5 min)</option>
                      <option value="24h">24 ore (1 ora)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Grafico storico temperatura */}
            <div className="col-12 dashboard-fade-in">
              <div className="dashboard-chart-card">
                <div className="dashboard-card-header-gradient d-flex align-items-center justify-content-between py-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up-arrow fs-5" />
                    <span className="fw-semibold">
                      Storico temperatura
                      {chartRange === '24h' ? ' (aggregazione oraria)' : ' (ogni 5 min)'}
                    </span>
                  </div>
                  <span className="badge bg-light text-dark">
                    {chartData.length} punti • agg. ogni ora
                  </span>
                </div>
                <div className="card-body" style={{ height: 320, minHeight: 200 }}>
                  {chartLoading ? (
                    <div className="text-muted">Caricamento grafico…</div>
                  ) : chartData.length === 0 ? (
                    <div className="text-muted">Nessun dato disponibile per il periodo selezionato.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={320} minHeight={200}>
                      <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="bucket"
                          stroke="#64748b"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => {
                            const d = new Date(v);
                            return Number.isNaN(d.getTime())
                              ? ''
                              : chartRange === '24h'
                                ? d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                                : d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                          }}
                        />
                        <YAxis stroke="#64748b" tick={{ fontSize: 11 }} unit="°C" />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                          labelFormatter={(v) => fmtDate(String(v))}
                          formatter={(val) => [`${val ?? '—'} °C`, 'Temperatura']}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#tempGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PRESENZE (mock per ora) */}
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="fw-semibold">Presenze oggi</div>
              <div className="d-flex gap-2">
                <span className="badge text-bg-success">Presenti: 9</span>
                <span className="badge text-bg-secondary">Assenti: 2</span>
                <span className="badge text-bg-warning">Ritardo: 1</span>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Ruolo</th>
                      <th>Entrata</th>
                      <th>Uscita</th>
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presences.map((p) => (
                      <tr key={p.name}>
                        <td className="fw-semibold">{p.name}</td>
                        <td className="text-muted">{p.role}</td>
                        <td>{p.inTime}</td>
                        <td>{p.outTime ?? '—'}</td>
                        <td>
                          <StatusBadge status={p.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 d-flex gap-2">
                <Link to="/personnel" className="btn btn-outline-primary btn-sm">
                  Gestisci presenze
                </Link>
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  Esporta report (in arrivo)
                </button>
              </div>

              <div className="mt-3">
                <div className="d-flex justify-content-between small text-muted">
                  <span>Copertura turni</span>
                  <span>87%</span>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div className="progress-bar" style={{ width: '87%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /PRESENZE */}
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon, variant = 'primary' }: { title: string; value: string; icon?: string; variant?: 'primary' | 'success' | 'warning' | 'danger' }) {
  const icons: Record<string, string> = {
    sales: 'bi-cash-coin',
    machines: 'bi-cpu',
    alarms: 'bi-exclamation-triangle',
    default: 'bi-graph-up-arrow',
  };
  
  const iconClass = icon ? icons[icon] || icons.default : icons.default;
  
  return (
    <div className="col-12 col-md-6 col-xl-3 dashboard-fade-in">
      <div className={`dashboard-kpi-card h-100 ${variant}`}>
        <div className="card-body p-4">
          <div className="dashboard-kpi-icon">
            <i className={`bi ${iconClass}`} />
          </div>
          <div className="dashboard-kpi-title">{title}</div>
          <div className="dashboard-kpi-value">{value}</div>
          <div className="mt-3">
            <div className="dashboard-progress-modern">
              <div className="dashboard-progress-bar-modern" style={{ width: '75%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'PRESENTE' | 'ASSENTE' | 'IN_RITARDO' | 'USCITO' }) {
  const cls =
    status === 'PRESENTE'
      ? 'text-bg-success'
      : status === 'ASSENTE'
        ? 'text-bg-secondary'
        : status === 'IN_RITARDO'
          ? 'text-bg-warning'
          : 'text-bg-info';

  return <span className={`badge ${cls}`}>{status}</span>;
}

function AlarmSeverityBadge({ severity }: { severity: string }) {
  const s = (severity || 'LOW').toUpperCase();
  const cls =
    s === 'CRITICAL' || s === 'HIGH'
      ? 'text-bg-danger'
      : s === 'MEDIUM'
        ? 'text-bg-warning'
        : 'text-bg-success';

  return <span className={`badge ${cls}`}>{s}</span>;
}